/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import React from 'react';
import isBoolean from 'lodash/isBoolean';

import { AvatarBadge, Box, Grid, Heading, Tooltip } from '@chakra-ui/core';
import { TreeMenuItem } from 'react-simple-tree-menu';
import { BsBookmarkStarFill } from 'react-icons/bs';
import { RiUserUnfollowFill } from 'react-icons/ri';
import { FaChevronCircleUp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { CandidateItemNode, NodeType } from '../treeGroup';
import { EngagedCandidate } from '../../../../../firebase/firestore/documents/candidate';
import { Label, ProfilePicture } from '../../../../common';
import colors from '../../../../../styles/colors';
import KisokIcon from '../../../../../assets/img/Kiosk.svg';
import AppyHereIcon from '../../../../../assets/img/icon-appyhere.svg';
import { useMessagingViewContext } from '../../../MessagingViewContext';
import { SeekersPresenter } from '../seekersPresenter';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { useCurrentUserProfile } from '../../../../../app/hooks/useCurrentUserProfile';
import { useUserDocumentSnapshot } from '../../../../../firebase/firestore/hooks';
import { useSeekersFilters } from '../../filters/SeekersFiltersContext';
import useLastMessage from './useLastMessage';
import { RemoteConfigKey } from '../../../../../firebase/remoteConfig';

type Props = {
  item: CandidateItemNode;
  selectedCallback: (candidate: EngagedCandidate) => void;
  isActive?: boolean;
  isShowFavoriteStatusEnabled: boolean;
  // eslint-disable-next-line react/require-default-props
  seekerList?: SeekersPresenter;
};

// istanbul ignore next
const addBoxClass = (item: TreeMenuItem) => {
  switch (item.type) {
    case NodeType.PARENT:
      return 'parent-block';
    case NodeType.CHILD:
      return 'child-block';
    default:
      return 'candidate-block';
  }
};

const NewCandidateItem: React.FC<Props> = ({
  item,
  selectedCallback,
  isActive,
  isShowFavoriteStatusEnabled,
  seekerList,
}: Props) => {
  const { t } = useTranslation();
  const [profileURL, setProfileURL] = React.useState<string>();
  const { selectedCandidateId } = useMessagingViewContext();
  const selectedSeekerId = selectedCandidateId?.split('_')[1];

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const itemSeekerId = (item.candidate?.id as string)?.split('_')[1];
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const isSelected = isBoolean(isActive) ? /* istanbul ignore next */ isActive : selectedSeekerId === itemSeekerId;
  const candidateBlockClass = `inner-candidate-block ${isSelected ? 'active-list' : /* istanbul ignore next */ ''}`;

  const isRead = item.candidate.accountNotification?.status?.[item.currentId]?.read ?? true;

  const lastMessage = useLastMessage(item.candidate.id);
  const seekersProfilePictures = useStoreState((state) => state.messaging.seekersProfilePictures);
  const loadSeekerNewProfilePicture = useStoreActions((actions) => actions.messaging.loadSeekerNewProfilePicture);
  const isUserDeactivated = Boolean(item.candidate.deactivated || item.candidate.scheduledDeletion);

  const { currentUserProfile } = useCurrentUserProfile();
  const user = useUserDocumentSnapshot(currentUserProfile?.id);

  const { ghostedListView, archiveListView, dismissListView } = useSeekersFilters();

  const minutesBeforeInactivity = useStoreState((state) => state.remoteConfig[RemoteConfigKey.MINUTES_BEFORE_INACTIVITY]);

  const recentlyActive =
    item.candidate.lastActivity &&
    moment(item.candidate.lastActivity).toDate() > moment().subtract(minutesBeforeInactivity, 'minute').toDate();

  const isSeekerPriority = (seekerID: string) => {
    const seekers = seekerList?.engagedCandidates?.filter((candidate) => candidate.seeker === seekerID);
    if (seekers) {
      for (let i = 0; i < seekers.length; i += 1) {
        if (seekers[i].priority) {
          return true;
        }
      }
    }
    return false;
  };

  const isSeekerFromKioskMode = (seekerID: string) => {
    const seekers = seekerList?.engagedCandidates?.filter((candidate) => candidate.seeker === seekerID);
    if (seekers) {
      for (let i = 0; i < seekers.length; i += 1) {
        if (seekers[i].kioskEnabled) {
          return true;
        }
      }
    }
    return false;
  };

  const isSeekerInterventionEnabled = (seekerID: string) => {
    const seekers = seekerList?.engagedCandidates?.filter((candidate) => candidate.seeker === seekerID);
    if (seekers) {
      for (let i = 0; i < seekers.length; i += 1) {
        if (seekers[i].interventionEnabled) {
          return true;
        }
      }
    }
    return false;
  };

  React.useEffect(() => {
    /* istanbul ignore else */
    if (item.candidate && item.candidate.displayProfilePicture) {
      loadSeekerNewProfilePicture({ candidate: item.candidate });
    }
  }, [item, loadSeekerNewProfilePicture]);

  React.useEffect(() => {
    const url = seekersProfilePictures[item.candidate.seekerId];
    setProfileURL(url);
  }, [item, seekersProfilePictures]);

  return (
    <Box
      className={addBoxClass(item)}
      data-testid="SeekerListItem"
      onClick={(e) => {
        e.stopPropagation();
        item.toggleNode?.();
      }}
    >
      <Box
        d="flex"
        alignItems="flex-start"
        className={candidateBlockClass}
        cursor="pointer"
        data-testid="CandidateItem"
        onClick={() => selectedCallback(item.candidate as EngagedCandidate)}
      >
        <Grid
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          className={selectedCandidateId === item.candidate?.id ? /* istanbul ignore next */ 'active-list' : ''}
          templateColumns={{ base: '48px auto', md: '50px auto', lg: '60px auto' }}
          width="100%"
          minH="48px"
        >
          <ProfilePicture
            src={profileURL}
            style={{ backgroundColor: 'orange', color: 'white' }}
            name={item.label}
            bg="transparent"
          >
            {recentlyActive && <AvatarBadge width="14px" height="14px" bg={colors.green['200']} />}
          </ProfilePicture>

          <Box pl="10px">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Heading
                as="h4"
                size="sm"
                color={isUserDeactivated ? 'rgba(0, 0, 0, 0.5)' : 'unset'}
                data-testid="FullName"
                display="inline-flex"
              >
                {item.label}
                {isSeekerPriority(item.candidate.seekerId) && (
                  <Tooltip hasArrow label={t('notification:seekerHeader.priorityTooltip')}>
                    <span style={{ marginLeft: '4px', display: 'flex', alignItems: 'center' }}>
                      <FaChevronCircleUp color={colors.green[600]} width="1rem" size={13} />
                    </span>
                  </Tooltip>
                )}
              </Heading>
              <Box d="flex" justifyContent="center" mt={1} pl={1}>
                {isSeekerFromKioskMode(item.candidate.seekerId) && (
                  <Tooltip hasArrow label={t('notification:seekerHeader.kioskTooltip')}>
                    <span
                      style={{
                        background: colors.blue['500'],
                        display: 'inline-block',
                        padding: '4px',
                        height: '18px',
                        width: '18px',
                        borderRadius: '50%',
                        marginLeft: '6px',
                      }}
                    >
                      <img alt="Kisko mode" src={KisokIcon} width="10px" />
                    </span>
                  </Tooltip>
                )}
                {isSeekerInterventionEnabled(item.candidate.seekerId) && (
                  <Tooltip hasArrow label={t('notification:seekerHeader.interventionTooltip')}>
                    <span
                      style={{
                        background: colors.blue['500'],
                        padding: '4px',
                        borderRadius: '50%',
                        marginLeft: '6px',
                        height: '18px',
                        width: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img alt="intervene" src={AppyHereIcon} width="auto" />
                    </span>
                  </Tooltip>
                )}

                {!isUserDeactivated &&
                  isShowFavoriteStatusEnabled &&
                  !dismissListView &&
                  !ghostedListView &&
                  !archiveListView &&
                  user?.favouriteSeekers?.map((seeker) =>
                    seeker.id === item.candidate.seekerId ? (
                      <Tooltip hasArrow label={t('notification:seekerHeader.favoriteCandidate')}>
                        <span
                          style={{
                            background: '#FF6600',
                            display: 'inline-block',
                            padding: '4px',
                            borderRadius: '50%',
                            marginLeft: '6px',
                          }}
                        >
                          <BsBookmarkStarFill data-testid="UnFavoriteCandidate" color="#ffffff" width="1rem" size={10} />
                        </span>
                      </Tooltip>
                    ) : (
                      ''
                    ),
                  )}

                {isUserDeactivated && (
                  <Tooltip hasArrow label={t('notification:seekerHeader.deactivatedUserLabel')}>
                    <span className="undismissIcon">
                      <RiUserUnfollowFill
                        data-testid="DeactivatedCandidateBadge"
                        color="rgba(0, 0, 0, 0.5)"
                        width="1rem"
                        size={20}
                      />
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Label
              bold={!isRead}
              color={isRead ? colors.neutralGray[400] : /* istanbul ignore next */ colors.neutralGray[700]}
              colorDarkMode={isRead ? colors.gray[100] : /* istanbul ignore next */ colors.white}
              whiteSpace="initial"
              paddingRight={0}
              noOfLines={2}
              dataTestId={lastMessage.id}
              style={{ wordBreak: 'break-word' }}
            >
              {lastMessage.lastMessage}
            </Label>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};

NewCandidateItem.defaultProps = {
  isActive: undefined,
};

export default NewCandidateItem;
